import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BomDesignerContext, DefaultMetadataFields } from "../../contexts/BomDesignerContext";
import { Asset, Point } from "../../contexts/SceneDesignerContext";
import ProductSelector from "./ProductSelector";
import "./BomDesigner.scss";
import { BASE_PATH } from "../..";
import { ReactComponent as AddIcon } from '../../assets/icons/add.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import classNames from "classnames";
import { ReactComponent as LoadingIcon } from '../../assets/icons/loading.svg';
import { AnimatePresence, motion } from "framer-motion";

const LoadingComponent = motion(LoadingIcon)

type Props = {
    onSave: () => void;
    hideSaveButton?: boolean;
}

const BomDesignPoint: React.FC<{ point: Point, onChangeClick: (point: Point) => void }> = ({ point, onChangeClick }) => {
    const { points, addExternalPoint, removeExternalPoint } = useContext(BomDesignerContext);
    const childPoints = points.filter(e => e.parent === point);

    const handleExternalToggle = useCallback(() => {
        if(point.hidden){
            addExternalPoint(point);
        }else{
            removeExternalPoint(point);
        }
    }, [addExternalPoint, point, removeExternalPoint]);

    return (
        <div className="point">
            <div>
                <h3>{point.label}</h3>
                <div className="asset">
                    <div className="asset-icon" onClick={() => onChangeClick(point)}>
                        {point.configuration.value === undefined ? <AddIcon /> : <img src={BASE_PATH + point.configuration.value.thumbnail} alt='' />}
                    </div>
                    <div className="asset-details">
                        <span>{point.configuration.value?.title}</span>
                        {point.parent !== undefined && <label><input onChange={handleExternalToggle} type="checkbox" checked={!point.hidden} />External</label>}
                        <button className="button primary" onClick={() => onChangeClick(point)}>{point.parent === undefined ? "Select whitemodel" : "Pick item"}</button>
                    </div>
                </div>
            </div>
            <div className="children">
                {childPoints.map(e => <BomDesignPoint point={e} onChangeClick={onChangeClick} />)}
            </div>
        </div>
    );
}

const BomDesigner: React.FC<Props> = ({onSave, hideSaveButton}) => {
    const { title, setTitle, points, changeProduct, loading, save, metadata, removeMetadata, editMetadata, addMetadata } = useContext(BomDesignerContext);
    const [selectedPoint, setSelectedPoint] = useState<Point | undefined>(undefined);
    const [selectorOpen, setSelectorOpen] = useState(false);
    const [selectedMetadataField, setSelectedMetadataField] = useState(DefaultMetadataFields[0]);
    const [titleValid, setTitleValid] = useState(true);

    const rootPoints = points.filter(e => e.parent === undefined);
    const rootPoint = points[0];

    useEffect(() => {
        setTitleValid(true);
    }, [points]);

    useEffect(() => {
        setTitleValid(!!title);
    }, [title]);

    const handlePickAsset = useCallback((point: Point) => {
        setSelectedPoint(point);
        setSelectorOpen(true);
    }, []);

    const handleCancel = useCallback(() => {
        setSelectorOpen(false);
    }, []);

    const handleAssetSelect = useCallback((assets: Asset[]) => {
        if (selectedPoint) {
            changeProduct(selectedPoint, [assets[0]], true);
        }
        setSelectorOpen(false);
    }, [changeProduct, selectedPoint]);

    const handleSave = useCallback(() => {
        if(title){
            save().then(onSave);
        }else{
            setTitleValid(false);
        }
    }, [onSave, save, title]);

    const VisiblePoints = useMemo(() => {
        if(rootPoint.bomData){
            return rootPoint.bomData.points.filter(e => points.some(p => p.label === e.targetName));
        }
        return [];
    }, [points, rootPoint.bomData]);

    const handleHotspotClick = useCallback((label: string) => {
        const point = points.find(e => e.label === label);
        if(point){
            setSelectedPoint(point);
            setSelectorOpen(true);
        }
    }, [points]);

    return (
        <div className="bomdesigner">
            <AnimatePresence>
                {loading && <LoadingComponent
                    className={'spinner'}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key={'spinner'} />}
            </AnimatePresence>
            
            <div className={classNames({"details": true, empty: rootPoint.configuration.value === undefined, "loading": loading})}>
                <label>Name</label><input className={titleValid ? "" : "invalid"} type="text" value={title} onChange={e => setTitle(e.target.value)} />

                <h2>Metadata</h2>

                {metadata.map((md, i) => <div className="metadatafield">
                    <label>{md.name}</label>
                    <div>
                        <input disabled={md.locked} onChange={e => editMetadata(i, { name: md.name, value: e.currentTarget.value, locked: false })} type="text" value={md.value} />
                        {!md.locked && <button onClick={() => removeMetadata(md)} className="button primary"><DeleteIcon /></button>}
                    </div>
                </div>)}

                <div className="new metadatafield">
                    <div>
                        <select onChange={e => setSelectedMetadataField(e.target.value)}>{DefaultMetadataFields.map(e => <option value={e}>{e}</option>)}</select>
                        <button onClick={e => addMetadata({ name: selectedMetadataField, value: "", locked: false })} className="button primary">Add</button>
                    </div>
                </div>

                {hideSaveButton !== true && <button onClick={handleSave} disabled={loading} className="button primary save">save</button>}
            </div>
            <div className={classNames({"image": true, empty: rootPoint.configuration.value === undefined, "loading": loading})}>
                {(rootPoint && rootPoint.bomData) &&
                    <div>
                        <img src={BASE_PATH + rootPoint.configuration.value?.thumbnail + "/512"} alt={rootPoint.configuration.value?.title} />
                        {VisiblePoints.map(e => <div onClick={() => handleHotspotClick(e.targetName)} className="hotspot" style={{top: `${e.posY*100}%`, left: `${e.posX*100}%`}}>{e.targetName}</div>)}
                    </div>
                }
            </div>
            <div className={classNames({"editor": true, empty: rootPoint.configuration.value === undefined, "loading": loading})}>
                {rootPoints.map(e => <BomDesignPoint point={e} onChangeClick={handlePickAsset} />)}

                <ProductSelector disableMultiSelect point={selectedPoint} open={selectorOpen} onCancel={handleCancel} onProductSelected={handleAssetSelect} />
            </div>

        </div>
    )
};

export default BomDesigner;